import api from './api';

export interface Expense {
  id: string;
  userId: string;
  description: string;
  amount: number;
  expenseDate: string;
  category: string;
  currency: string;
  vendor?: string;
  notes?: string;
  receiptPath?: string;
  status?: 'draft' | 'submitted' | 'approved' | 'rejected';
  rejectionReason?: string;
  expenseType?: 'regular' | 'standing_order' | 'capital_expenditure';
  standingOrderFrequency?: 'monthly' | 'quarterly' | 'yearly' | null;
  standingOrderPaymentDay?: number | null;
  standingOrderDescriptionPattern?: string | null;
  usefulLifeYears?: number | null;
  depreciationMethod?: 'straight_line' | 'declining_balance' | 'units_of_production' | null;
  salvageValue?: number | null;
  reconciledAt?: string | null;
  createdAt: string;
}

export interface CreateExpenseData {
  description: string;
  amount: number;
  expenseDate: string;
  category: string;
  currency: string;
  vendor?: string;
  notes?: string;
  receipt?: File;
  expenseType?: 'regular' | 'standing_order' | 'capital_expenditure';
  standingOrderFrequency?: 'monthly' | 'quarterly' | 'yearly';
  standingOrderPaymentDay?: number;
  usefulLifeYears?: number;
  depreciationMethod?: 'straight_line' | 'declining_balance' | 'units_of_production';
  salvageValue?: number;
}

export interface UpdateExpenseData {
  description?: string;
  amount?: number;
  expenseDate?: string;
  category?: string;
  currency?: string;
  vendor?: string;
  notes?: string;
  receipt?: File;
  expenseType?: 'regular' | 'standing_order' | 'capital_expenditure';
  standingOrderFrequency?: 'monthly' | 'quarterly' | 'yearly';
  standingOrderPaymentDay?: number;
  usefulLifeYears?: number;
  depreciationMethod?: 'straight_line' | 'declining_balance' | 'units_of_production';
  salvageValue?: number;
}

export interface ExpenseFilters {
  startDate?: string;
  endDate?: string;
  category?: string;
  status?: 'draft' | 'submitted' | 'approved' | 'rejected';
  expenseType?: 'regular' | 'standing_order' | 'capital_expenditure';
}

export const expensesApi = {
  getAll: async (filters?: ExpenseFilters): Promise<Expense[]> => {
    const params = new URLSearchParams();
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);
    if (filters?.category) params.append('category', filters.category);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.expenseType) params.append('expenseType', filters.expenseType);

    const queryString = params.toString();
    const url = `/expenses${queryString ? `?${queryString}` : ''}`;
    const response = await api.get(url);
    return response.data.data;
  },

  getById: async (id: string): Promise<Expense> => {
    const response = await api.get(`/expenses/${id}`);
    return response.data.data;
  },

  create: async (data: CreateExpenseData): Promise<Expense> => {
    const formData = new FormData();
    formData.append('description', data.description);
    formData.append('amount', data.amount.toString());
    formData.append('expenseDate', data.expenseDate);
    formData.append('category', data.category);
    formData.append('currency', data.currency);
    if (data.vendor) formData.append('vendor', data.vendor);
    if (data.notes) formData.append('notes', data.notes);
    if (data.expenseType) formData.append('expenseType', data.expenseType);
    if (data.standingOrderFrequency) formData.append('standingOrderFrequency', data.standingOrderFrequency);
    if (data.standingOrderPaymentDay !== undefined) formData.append('standingOrderPaymentDay', data.standingOrderPaymentDay.toString());
    if (data.usefulLifeYears !== undefined) formData.append('usefulLifeYears', data.usefulLifeYears.toString());
    if (data.depreciationMethod) formData.append('depreciationMethod', data.depreciationMethod);
    if (data.salvageValue !== undefined) formData.append('salvageValue', data.salvageValue.toString());
    if (data.receipt) formData.append('receipt', data.receipt);

    const response = await api.post('/expenses', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data.data;
  },

  update: async (id: string, data: UpdateExpenseData): Promise<Expense> => {
    const formData = new FormData();
    if (data.description !== undefined) formData.append('description', data.description);
    if (data.amount !== undefined) formData.append('amount', data.amount.toString());
    if (data.expenseDate !== undefined) formData.append('expenseDate', data.expenseDate);
    if (data.category !== undefined) formData.append('category', data.category);
    if (data.currency !== undefined) formData.append('currency', data.currency);
    if (data.vendor !== undefined) formData.append('vendor', data.vendor || '');
    if (data.notes !== undefined) formData.append('notes', data.notes || '');
    if (data.expenseType !== undefined) formData.append('expenseType', data.expenseType);
    if (data.standingOrderFrequency !== undefined) formData.append('standingOrderFrequency', data.standingOrderFrequency || '');
    if (data.standingOrderPaymentDay !== undefined) formData.append('standingOrderPaymentDay', data.standingOrderPaymentDay?.toString() || '');
    if (data.usefulLifeYears !== undefined) formData.append('usefulLifeYears', data.usefulLifeYears?.toString() || '');
    if (data.depreciationMethod !== undefined) formData.append('depreciationMethod', data.depreciationMethod || '');
    if (data.salvageValue !== undefined) formData.append('salvageValue', data.salvageValue?.toString() || '');
    if (data.receipt) formData.append('receipt', data.receipt);

    const response = await api.put(`/expenses/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data.data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/expenses/${id}`);
  },

  submit: async (id: string): Promise<Expense> => {
    const response = await api.put(`/expenses/${id}/submit`);
    return response.data.data;
  },

  getReceiptUrl: (receiptPath: string): string => {
    // Receipts are served from /api/uploads/
    // The receiptPath from backend is relative to uploads directory (e.g., "receipts/receipt-123.jpg")
    const baseURL = api.defaults.baseURL || 'http://localhost:3001/api';
    const path = receiptPath.startsWith('/') ? receiptPath : `/uploads/${receiptPath}`;
    return `${baseURL}${path}`;
  },
};

